/* eslint-disable no-irregular-whitespace */
import { Spacing, Text } from '@vkontakte/vkui';
import darkCover from 'img/vkmusic-bundle/vkmusic-bundle-bye-modal/free/dark.png';
import lightCover from 'img/vkmusic-bundle/vkmusic-bundle-bye-modal/free/light.png';
import React, { memo, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { WhatsNewDialog } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog';
import { isCurrentDarkTheme } from 'reactApp/modules/theme/theme.selectors';
import { type VK_MUSIC_BUNDLE_SOURCE, VK_MUSIC_RADAR_NAMES } from 'reactApp/modules/vkMusic/vkMusic.const';
import type { VkMusicBundleTariffInfo } from 'reactApp/modules/vkMusic/vkMusic.types';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './VkMusicSuccessModal.css';

export interface VkMusicSuccessModalProps {
    onClose?: () => void;
    onPrimaryClick?: () => void;
    onSecondaryClick?: () => void;
    onShow?: () => void;
    onCancel?: () => void;
    tariffInfo: VkMusicBundleTariffInfo;
    source: VK_MUSIC_BUNDLE_SOURCE;
}

export const VkMusicSuccessModal = memo<VkMusicSuccessModalProps>(
    ({ onShow, onPrimaryClick, onSecondaryClick, onClose, onCancel, tariffInfo, source }) => {
        const isDarkTheme = useSelector(isCurrentDarkTheme);
        const { space } = tariffInfo;

        const sendAnalytics = useCallback((action: string, restArgs = {}) => {
            sendPaymentGa({
                action,
                name: VK_MUSIC_RADAR_NAMES.SUCCESS_MODAL,
                eventCategory: ECategoryGa.modal,
                offer_source: source,
                ...restArgs,
            });
        }, []);

        useEffect(() => {
            sendAnalytics('show');
            emitAnalyticEvent(AnalyticEventNames.VK_MUSIC_SUCCESS_SHOW, { source });
            onShow?.();
        }, []);

        const handleOnPrimaryClick = useCallback(() => {
            sendAnalytics('click', { button_name: 'to_music' });
            emitAnalyticEvent(AnalyticEventNames.VK_MUSIC_SUCCESS_CLICK, { type: 'to_music', source });
            onPrimaryClick?.();
        }, []);

        const handleOnSecondaryClick = useCallback(() => {
            sendAnalytics('click', { button_name: 'to_cloud' });
            emitAnalyticEvent(AnalyticEventNames.VK_MUSIC_SUCCESS_CLICK, { type: 'to_cloud', source });
            onSecondaryClick?.();
        }, []);

        const handleOnCancel = useCallback(() => {
            sendAnalytics('close');
            emitAnalyticEvent(AnalyticEventNames.VK_MUSIC_SUCCESS_CLOSE, { source });
            onCancel?.();
        }, []);

        return (
            <WhatsNewDialog
                className={styles.root}
                imageUrl={isDarkTheme ? darkCover : lightCover}
                imageWrapperClassName={styles.imageWrapper}
                imageClassName={styles.image}
                contentClassName={styles.content}
                title="Шалость удалась! Mail Space и VK Музыка ваши"
                content={
                    <>
                        <Spacing size={16} />
                        <Text>
                            Теперь фото и видео с телефона не займут место в Облаке, а для остального будет {space.value}.
                            А в VK Музыке можно слушать треки и аудиокниги с выключенным экраном и без интернета
                        </Text>
                        <Spacing size={32} />
                    </>
                }
                closeIconClassName={styles.cross}
                primaryButtonText="Перейти в VK Музыку"
                secondaryButtonText="Остаться в Облаке"
                closeBySecondaryButton
                closeAfterClick
                onClick={handleOnPrimaryClick}
                onSecondary={handleOnSecondaryClick}
                onClose={onClose}
                onDismiss={handleOnCancel}
                imageBgColor="var(--vkui--color_background_modal)"
                buttonTheme="vk"
                dialogSize="large"
                qaId="vkmusic_bundle_success_modal"
                isRebranding
            />
        );
    }
);

VkMusicSuccessModal.displayName = 'VkMusicSuccessModal';
